import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { motion } from 'framer-motion';
import { User, MapPin, Bike, Plus, Edit2, Trash2, Save, X } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { v4 as uuidv4 } from 'uuid';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Profile = () => {
  const { t, i18n } = useTranslation();
  const { user, token } = useAuth();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [motoDialogOpen, setMotoDialogOpen] = useState(false);
  const [editingMoto, setEditingMoto] = useState(null);
  
  const [formData, setFormData] = useState({
    bio: '',
    location: '',
    country: 'FR',
    avatar_url: '',
  });

  const [motoForm, setMotoForm] = useState({
    brand: '',
    model: '',
    year: new Date().getFullYear(),
    engine_cc: '',
    image_url: '',
  });

  const countries = [
    { value: 'FR', label: t('countries.FR') },
    { value: 'DE', label: t('countries.DE') },
    { value: 'ES', label: t('countries.ES') },
    { value: 'IT', label: t('countries.IT') },
    { value: 'BE', label: t('countries.BE') },
    { value: 'CH', label: t('countries.CH') },
    { value: 'NL', label: t('countries.NL') },
    { value: 'PT', label: t('countries.PT') },
    { value: 'UK', label: t('countries.UK') },
  ];

  useEffect(() => {
    fetchProfile();
  }, []);

  const fetchProfile = async () => {
    try {
      const response = await axios.get(`${API_URL}/profiles/me`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setProfile(response.data);
      setFormData({
        bio: response.data.bio || '',
        location: response.data.location || '',
        country: response.data.country || 'FR',
        avatar_url: response.data.avatar_url || '',
      });
    } catch (error) {
      console.error('Error fetching profile:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveProfile = async () => {
    try {
      const payload = {
        ...formData,
        motorcycles: profile?.motorcycles || [],
      };
      
      await axios.put(`${API_URL}/profiles/me`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });
      
      toast.success(t('common.success'));
      setEditing(false);
      fetchProfile();
    } catch (error) {
      console.error('Error updating profile:', error);
      toast.error(t('common.error'));
    }
  };

  const handleAddMotorcycle = async () => {
    try {
      const newMoto = {
        id: uuidv4(),
        ...motoForm,
        year: parseInt(motoForm.year),
        engine_cc: motoForm.engine_cc ? parseInt(motoForm.engine_cc) : null,
      };

      let updatedMotorcycles;
      if (editingMoto) {
        updatedMotorcycles = profile.motorcycles.map(m => 
          m.id === editingMoto.id ? { ...newMoto, id: editingMoto.id } : m
        );
      } else {
        updatedMotorcycles = [...(profile?.motorcycles || []), newMoto];
      }

      const payload = {
        ...formData,
        motorcycles: updatedMotorcycles,
      };

      await axios.put(`${API_URL}/profiles/me`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });

      toast.success(t('common.success'));
      setMotoDialogOpen(false);
      setEditingMoto(null);
      setMotoForm({
        brand: '',
        model: '',
        year: new Date().getFullYear(),
        engine_cc: '',
        image_url: '',
      });
      fetchProfile();
    } catch (error) {
      console.error('Error adding motorcycle:', error);
      toast.error(t('common.error'));
    }
  };

  const handleEditMotorcycle = (moto) => {
    setEditingMoto(moto);
    setMotoForm({
      brand: moto.brand,
      model: moto.model,
      year: moto.year,
      engine_cc: moto.engine_cc || '',
      image_url: moto.image_url || '',
    });
    setMotoDialogOpen(true);
  };

  const handleDeleteMotorcycle = async (motoId) => {
    try {
      const updatedMotorcycles = profile.motorcycles.filter(m => m.id !== motoId);
      
      const payload = {
        ...formData,
        motorcycles: updatedMotorcycles,
      };

      await axios.put(`${API_URL}/profiles/me`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });

      toast.success(t('common.success'));
      fetchProfile();
    } catch (error) {
      console.error('Error deleting motorcycle:', error);
      toast.error(t('common.error'));
    }
  };

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto px-4 md:px-8 py-8">
        <div className="animate-pulse space-y-6">
          <div className="h-32 bg-muted rounded-lg" />
          <div className="h-48 bg-muted rounded-lg" />
          <div className="h-64 bg-muted rounded-lg" />
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-4 md:px-8 py-8">
      <h1 className="font-heading font-bold text-3xl md:text-4xl uppercase tracking-tight mb-8">
        {t('profile.title')}
      </h1>

      {/* Profile Card */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
      >
        <Card className="mb-8">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="font-heading font-bold text-xl uppercase">
              {i18n.language === 'fr' ? 'Informations' : 'Information'}
            </CardTitle>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => editing ? handleSaveProfile() : setEditing(true)}
              data-testid={editing ? "save-profile-btn" : "edit-profile-btn"}
            >
              {editing ? (
                <>
                  <Save className="w-4 h-4 mr-2" />
                  {t('common.save')}
                </>
              ) : (
                <>
                  <Edit2 className="w-4 h-4 mr-2" />
                  {t('profile.edit')}
                </>
              )}
            </Button>
          </CardHeader>
          <CardContent>
            <div className="flex flex-col md:flex-row gap-6">
              {/* Avatar */}
              <div className="flex-shrink-0">
                <div 
                  className="w-24 h-24 md:w-32 md:h-32 rounded-full bg-primary/20 flex items-center justify-center overflow-hidden"
                  style={formData.avatar_url ? { backgroundImage: `url(${formData.avatar_url})`, backgroundSize: 'cover' } : {}}
                >
                  {!formData.avatar_url && <User className="w-12 h-12 text-primary" />}
                </div>
              </div>

              {/* Info */}
              <div className="flex-1 space-y-4">
                <div>
                  <p className="font-heading font-bold text-2xl">{profile?.username}</p>
                  <p className="text-muted-foreground">{user?.email}</p>
                </div>

                {editing ? (
                  <div className="space-y-4">
                    <div className="space-y-2">
                      <Label>{t('profile.bio')}</Label>
                      <Textarea
                        value={formData.bio}
                        onChange={(e) => setFormData({ ...formData, bio: e.target.value })}
                        placeholder={i18n.language === 'fr' ? 'Parlez-nous de vous...' : 'Tell us about yourself...'}
                        rows={3}
                        data-testid="bio-input"
                      />
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>{t('profile.location')}</Label>
                        <Input
                          value={formData.location}
                          onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                          placeholder="Paris"
                          data-testid="location-input"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>{t('profile.country')}</Label>
                        <Select 
                          value={formData.country} 
                          onValueChange={(v) => setFormData({ ...formData, country: v })}
                        >
                          <SelectTrigger data-testid="country-select">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {countries.map((c) => (
                              <SelectItem key={c.value} value={c.value}>
                                {c.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label>{i18n.language === 'fr' ? 'URL Avatar' : 'Avatar URL'}</Label>
                      <Input
                        value={formData.avatar_url}
                        onChange={(e) => setFormData({ ...formData, avatar_url: e.target.value })}
                        placeholder="https://..."
                        data-testid="avatar-url-input"
                      />
                    </div>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {profile?.bio && (
                      <p className="text-muted-foreground">{profile.bio}</p>
                    )}
                    <div className="flex items-center gap-2 text-muted-foreground">
                      <MapPin className="w-4 h-4" />
                      <span>
                        {profile?.location || i18n.language === 'fr' ? 'Non renseigné' : 'Not specified'}
                        {profile?.country && `, ${t(`countries.${profile.country}`)}`}
                      </span>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      </motion.div>

      {/* My Garage */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
      >
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="font-heading font-bold text-xl uppercase flex items-center gap-2">
              <Bike className="w-5 h-5 text-primary" />
              {t('profile.myGarage')}
            </CardTitle>
            <Dialog open={motoDialogOpen} onOpenChange={(open) => {
              setMotoDialogOpen(open);
              if (!open) {
                setEditingMoto(null);
                setMotoForm({
                  brand: '',
                  model: '',
                  year: new Date().getFullYear(),
                  engine_cc: '',
                  image_url: '',
                });
              }
            }}>
              <DialogTrigger asChild>
                <Button size="sm" className="gap-2" data-testid="add-motorcycle-btn">
                  <Plus className="w-4 h-4" />
                  {t('profile.addMotorcycle')}
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle className="font-heading font-bold text-xl uppercase">
                    {editingMoto 
                      ? (i18n.language === 'fr' ? 'Modifier la moto' : 'Edit motorcycle')
                      : t('profile.addMotorcycle')}
                  </DialogTitle>
                </DialogHeader>
                <div className="space-y-4 mt-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>{i18n.language === 'fr' ? 'Marque' : 'Brand'} *</Label>
                      <Input
                        value={motoForm.brand}
                        onChange={(e) => setMotoForm({ ...motoForm, brand: e.target.value })}
                        placeholder="Honda"
                        required
                        data-testid="moto-brand-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>{i18n.language === 'fr' ? 'Modèle' : 'Model'} *</Label>
                      <Input
                        value={motoForm.model}
                        onChange={(e) => setMotoForm({ ...motoForm, model: e.target.value })}
                        placeholder="CB500F"
                        required
                        data-testid="moto-model-input"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>{i18n.language === 'fr' ? 'Année' : 'Year'} *</Label>
                      <Input
                        type="number"
                        value={motoForm.year}
                        onChange={(e) => setMotoForm({ ...motoForm, year: e.target.value })}
                        min="1900"
                        max={new Date().getFullYear() + 1}
                        required
                        data-testid="moto-year-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>{i18n.language === 'fr' ? 'Cylindrée (cc)' : 'Engine (cc)'}</Label>
                      <Input
                        type="number"
                        value={motoForm.engine_cc}
                        onChange={(e) => setMotoForm({ ...motoForm, engine_cc: e.target.value })}
                        placeholder="500"
                        data-testid="moto-cc-input"
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label>{i18n.language === 'fr' ? "URL de l'image" : 'Image URL'}</Label>
                    <Input
                      type="url"
                      value={motoForm.image_url}
                      onChange={(e) => setMotoForm({ ...motoForm, image_url: e.target.value })}
                      placeholder="https://..."
                      data-testid="moto-image-input"
                    />
                  </div>
                  <Button 
                    className="w-full" 
                    onClick={handleAddMotorcycle}
                    disabled={!motoForm.brand || !motoForm.model}
                    data-testid="save-motorcycle-btn"
                  >
                    {t('common.save')}
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
          </CardHeader>
          <CardContent>
            {profile?.motorcycles?.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {profile.motorcycles.map((moto, index) => (
                  <motion.div
                    key={moto.id}
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: index * 0.1 }}
                  >
                    <Card className="group relative overflow-hidden" data-testid={`motorcycle-card-${moto.id}`}>
                      <div 
                        className="h-40 bg-cover bg-center"
                        style={{
                          backgroundImage: moto.image_url 
                            ? `url('${moto.image_url}')` 
                            : `url('https://images.unsplash.com/photo-1629628083289-009e6f87f5cf?crop=entropy&cs=srgb&fm=jpg&q=85')`,
                        }}
                      >
                        <div className="absolute inset-0 bg-gradient-to-t from-card to-transparent" />
                      </div>
                      <CardContent className="p-4 relative">
                        <div className="flex justify-between items-start">
                          <div>
                            <h3 className="font-heading font-bold text-lg uppercase">
                              {moto.brand} {moto.model}
                            </h3>
                            <p className="text-muted-foreground font-mono text-sm">
                              {moto.year} {moto.engine_cc && `• ${moto.engine_cc}cc`}
                            </p>
                          </div>
                          <div className="flex gap-1">
                            <Button 
                              variant="ghost" 
                              size="icon" 
                              className="h-8 w-8"
                              onClick={() => handleEditMotorcycle(moto)}
                              data-testid={`edit-moto-${moto.id}`}
                            >
                              <Edit2 className="w-4 h-4" />
                            </Button>
                            <Button 
                              variant="ghost" 
                              size="icon" 
                              className="h-8 w-8 text-destructive hover:text-destructive"
                              onClick={() => handleDeleteMotorcycle(moto.id)}
                              data-testid={`delete-moto-${moto.id}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </motion.div>
                ))}
              </div>
            ) : (
              <div className="text-center py-12">
                <Bike className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
                <p className="text-muted-foreground">
                  {i18n.language === 'fr' 
                    ? 'Aucune moto dans votre garage' 
                    : 'No motorcycles in your garage'}
                </p>
                <Button 
                  className="mt-4" 
                  onClick={() => setMotoDialogOpen(true)}
                  data-testid="add-first-motorcycle-btn"
                >
                  {t('profile.addMotorcycle')}
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      </motion.div>
    </div>
  );
};

export default Profile;
